import { useLocation, useNavigate } from "react-router-dom";
import { exhibitionFilterStore } from "./useExhibitionFilters";
import { useQuery } from "@apollo/client";
import {
  FETCH_EXHIBITION,
  FETCH_EXHIBITIONS_SEARCH_SYNOPSIS,
} from "../../../graphql/query/Exhibition";
import { FETCH_LIST as fetchList } from "../../../graphql/query/List";
import { Breadcrumb, Button, MenuButton, Modal } from "../../../components/ui";
import { GenerateReportBody } from "../../../components/Modals/GenerateReport";
import NewExhibition from "./Actions/NewExhibition";
import ExhibitionSearch from "./ExhibitionSearch";
import routes from "../pages/Wizard/routes";
import DuplicateExhibition from "./Actions/DuplicateExhibition";
import CopyDescription from "./Actions/CopyDescription";
import useUser from "../../../hooks/useUser";
import ConsignWork from "./Actions/ConsignWork";
import NewJob from "../../Jobs/components/NewJob";
import AddShipment from "../../Shipment/components/AddShipment";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import exportModule from "./Actions/ExportModule";
import AdhocReportBody from "./Actions/AdhocReportBody";
import { useState } from "react";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";

const PrimaryNav = props => {
  const { formik } = props;
  const search = exhibitionFilterStore.get();
  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location?.pathname.split("/");
  const id = pathnames?.length > 2 ? pathnames[2] : null;
  const { hasPermission } = useUser();

  const exhibitionObj = {
    contact: {},
    exhibitionWorks: false,
  };
  const { data: { exhibition } = {} } = useQuery(
    FETCH_EXHIBITION(exhibitionObj),
    {
      skip: !id,
      variables: { id: +id },
    },
  );
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;
  
  // Fetch list names when viewing saved lists (only if listId/imageListId is explicitly in URL and is a valid number)
  const { data: { list: savedList } = {} } = useQuery(
    fetchList(),
    {
      variables: { id: listId },
      skip: !listIdParam || !listId || isNaN(listId) || listId <= 0, // Only fetch if listId is explicitly in URL and valid
      errorPolicy: "ignore", // Ignore errors if list doesn't exist
    }
  );
  
  const { data: { list: savedImageList } = {} } = useQuery(
    fetchList(),
    {
      variables: { id: imageListId },
      skip: !imageListIdParam || !imageListId || isNaN(imageListId) || imageListId <= 0, // Only fetch if imageListId is explicitly in URL and valid
      errorPolicy: "ignore", // Ignore errors if list doesn't exist
    }
  );

  const statusMap = {
    all: "All Exhibitions",
    searched: "Searched Exhibitions",
    selectListWorking: listIdParam && savedList?.name
      ? `Select List (${savedList.name})`
      : "Select List (Working)",
    imageSelectListWorking: imageListIdParam && savedImageList?.name
      ? `Image Select List (${savedImageList.name})`
      : "Image Select List (Working)",
  };
  const statusLabel = statusMap[status] || "All Exhibitions";

  const hasSearch =
    search != null &&
    typeof search === "object" &&
    !Array.isArray(search) &&
    Object.keys(search).length > 0;
  const { data: { exhibitionsV2: { searchSynopsis } = {} } = {} } = useQuery(
    FETCH_EXHIBITIONS_SEARCH_SYNOPSIS,
    {
      skip: !hasSearch,
      variables: { input: { ...(search || {}), limit: 1 } },
      fetchPolicy: "cache-first",
    },
  );
  // The breadcrumb root usually shows the active list ("All Exhibitions"
  // / "Searched Exhibitions" / saved list name), but when the user has
  // an active Exhibition Search we substitute the human-readable
  // synopsis there so the page makes it obvious *what* they're looking
  // at. Mirrors the contacts module: bold "Exhibition Search" prefix is
  // set here (rather than in the API string) so the framing is styled,
  // and `normalCase` keeps connectors like "between" / "ordered by"
  // lowercase / wrapping freely. `disabled` because clicking the
  // synopsis would only navigate back to the (unfiltered) list, which
  // isn't the affordance the text suggests.
  const rootLabel = searchSynopsis
    ? {
        label: (
          <>
            <span className="font-bold">Exhibition Search</span> {searchSynopsis}
          </>
        ),
        title: `Exhibition Search ${searchSynopsis}`,
        normalCase: true,
        disabled: true,
      }
    : statusLabel;
  const steps = [
    { label: rootLabel, onClick: () => navigate("/exhibition") },
    ...(exhibition
      ? [
          {
            label: exhibition?.title,
            onClick: () => navigate(`${exhibition?.id}/details`),
          },
          {
            label: route?.name,
            onClick: () => navigate(`${exhibition?.id}/${route?.href}`),
          },
        ]
      : []),
  ];

  const searchModalProps = {
    body: ExhibitionSearch,
    closeOnBackdrop: true,
    scale: "md",
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // like Export here too. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: id,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Exhibition",
        disabled: !hasPermission("UPDATE_EXHIBITION"),
        modalProps: {
          body: NewExhibition,
          disabled: !hasPermission("UPDATE_EXHIBITION"),
          ...defaultActionProps,
        },
      },
      {
        label: "Export Exhibition Report",
        modalProps: {
          body: ExportActiveReport,
          obj: {
            ...exhibitionObj,
            contact: {
              contactCompany: {},
            },
            exhibitionWorks: {
              reportData: true,
            },
          },
          type: "Exhibition",
          ids: exhibition?.id ? [exhibition?.id] : ids,
          ...defaultActionProps,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !id,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !ids.length && !id,
        label: (
          <>
            New Reporting{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${
                !ids.length && !id
                  ? "bg-gray-100 text-gray-400"
                  : "bg-green-100 text-green-700"
              }`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Exhibition",
          onClose: () => {},
        },
      },
      ...(id
        ? [
            {
              label: "Duplicate Exhibition",
              modalProps: {
                body: DuplicateExhibition,
                closeOnBackdrop: true,
                exhibition,
                hideCloseButton: true,
                scale: "sm",
              },
            },
            {
              label: "Copy Description",
              modalProps: {
                title: "Copy description?",
                description:
                  "Do you wish to copy the description from the exhibition?",
                scale: "sm",
                body: CopyDescription,
                closeOnBackdrop: true,
                exhibitionId: exhibition?.id,
                hideCloseButton: true,
              },
            },
            {
              label: "Consign Work",
              disabled: exhibition?.exhibitionWorks?.length < 1,
              modalProps: {
                scale: "md",
                body: ConsignWork,
                closeOnBackdrop: true,
                hideCloseButton: true,
                exhibition: exhibition,
              },
            },
            {
              label: "Loan Work",
              disabled: exhibition?.exhibitionWorks?.length < 1,
              modalProps: {
                scale: "md",
                body: ConsignWork,
                closeOnBackdrop: true,
                hideCloseButton: true,
                exhibition: exhibition,
                typeId: 2,
              },
            },
            {
              label: "Ship Work",
              disabled: exhibition?.exhibitionWorks?.length < 1,
              modalProps: {
                scale: "md",
                body: AddShipment,
                closeOnBackdrop: true,
                hideCloseButton: true,
                exhibition: exhibition,
              },
            },
            {
              disabled:
                !hasPermission("CREATE_JOB") ||
                exhibition?.exhibitionWorks?.length < 1,
              label: "Create Job",
              modalProps: {
                body: NewJob,
                scale: "md",
                exhibitionId: id,
                closeOnBackdrop: false,
                hideCloseButton: true,
              },
            },
          ]
        : []),
    ],
  };

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b px-8 py-5">
      {/*
        `min-w-0` lets the breadcrumb shrink inside the flex row so a
        long search synopsis wraps onto its own line(s) instead of
        pushing the right-hand actions cluster off-screen. `flex-1`
        claims any leftover horizontal space.
      */}
      <div className="min-w-0 flex-1">
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex flex-shrink-0 justify-end gap-4">
        <Modal {...searchModalProps}>
          <Button label="Exhibition Search" action="black" />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </div>
  );
};

export default PrimaryNav;
